import type { SpaceMembersSnapshot } from "@wakeloop/schemas/types";
import {
  assertValidSpaceCanonicalMentionSyntax,
  type PreparedSpaceComposerSubmission,
  prepareSpaceComposerSubmission,
  type SpaceComposerReplyTarget,
  spaceTextHasOneShotMentionAttempts,
} from "@wakeloop/space";

export type PreparedCliSpaceComposerSubmission = {
  outboundMessageEnvelope?: NonNullable<
    PreparedSpaceComposerSubmission["messageEnvelope"]
  >;
  outboundReplyTo?: SpaceComposerReplyTarget;
  outboundText: string;
  preparedSubmission: PreparedSpaceComposerSubmission;
};

export async function prepareCliSpaceComposerSubmission(input: {
  missingSnapshotMessage: string;
  onMembersSnapshotLoaded?: (snapshot: SpaceMembersSnapshot) => void;
  onSnapshotLoadFailure?: (error: unknown) => void;
  replyTo?: SpaceComposerReplyTarget | null;
  loadMembersSnapshot?: () => Promise<SpaceMembersSnapshot | null>;
  text: string;
}): Promise<PreparedCliSpaceComposerSubmission> {
  const hasCanonicalMentionAttempts = spaceTextHasOneShotMentionAttempts(
    input.text
  );
  if (hasCanonicalMentionAttempts) {
    assertValidSpaceCanonicalMentionSyntax(input.text);
  }

  let membersSnapshot: SpaceMembersSnapshot | null = null;
  if (hasCanonicalMentionAttempts && input.loadMembersSnapshot) {
    try {
      membersSnapshot = await input.loadMembersSnapshot();
      if (membersSnapshot) {
        input.onMembersSnapshotLoaded?.(membersSnapshot);
      }
    } catch (error) {
      input.onSnapshotLoadFailure?.(error);
      throw error;
    }
  }

  const preparedSubmission = prepareSpaceComposerSubmission({
    missingSnapshotMessage: input.missingSnapshotMessage,
    replyTo: input.replyTo,
    snapshot: membersSnapshot,
    text: input.text,
  });

  return {
    ...(preparedSubmission.messageEnvelope
      ? { outboundMessageEnvelope: preparedSubmission.messageEnvelope }
      : {}),
    ...(preparedSubmission.replyTo
      ? { outboundReplyTo: preparedSubmission.replyTo }
      : {}),
    outboundText: preparedSubmission.text,
    preparedSubmission,
  };
}
